import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useStatusOperations } from "./useStatusOperations";

interface StatusDialogProps {
  status: { id: string; name: string; description: string | null } | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const StatusDialog = ({ status, open, onClose, onSuccess }: StatusDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const { createStatus, updateStatus } = useStatusOperations();

  useEffect(() => {
    if (status) {
      setFormData({
        name: status.name,
        description: status.description || "",
      });
    } else {
      setFormData({
        name: "",
        description: "",
      });
    }
  }, [status]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (status) {
        await updateStatus(status.id, formData);
      } else {
        await createStatus(formData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving status:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {status ? "Edit Status" : "Add New Status"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {status ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};